"use client";

import { computeCharges } from "@/lib/charges";
import type { PropertyCharge } from "@/lib/types";
import React, { useState, useEffect, useMemo } from "react";
import { useApp } from "./AppContext";
import { GuestIcon, SearchIcon, XIcon } from "./Icons";
import { GuestPickerDialog, type PickedGuest } from "./GuestPickerDialog";
import { DateRangeField } from "./DateField";
import { getBusinessDate } from "@/lib/date-utils";
import type { Unit, UnitGroup, BookingSource, CatalogItem, AddonBasis } from "@/lib/types";

/** The unit select's value for "any free plot of a unit type". */
const GROUP_PREFIX = "group:";
import { toMinorUnits, formatMoney, getCurrencyDecimals } from "@/lib/money";
import { useI18n } from "@/i18n/I18nProvider";

interface NewBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialPropertyId?: string;
  initialUnitId?: string;
  initialCheckIn?: string;
}

const SOURCES: BookingSource[] = [
  "WHATSAPP",
  "WEBSITE",
  "PHONE",
  "WALK_IN",
  "OTA",
  "OWNER",
  "OTHER",
];

export function NewBookingModal({
  isOpen,
  onClose,
  onSuccess,
  initialPropertyId,
  initialUnitId,
  initialCheckIn,
}: NewBookingModalProps) {
  const { properties, fetchWithTenant } = useApp();
  const { t, tx } = useI18n();

  const [propertyId, setPropertyId] = useState<string>("");
  const [unitId, setUnitId] = useState<string>("");
  const [units, setUnits] = useState<Unit[]>([]);
  const [groups, setGroups] = useState<UnitGroup[]>([]);
  const [guestMode, setGuestMode] = useState<"new" | "existing">("new");
  const [selectedGuest, setSelectedGuest] = useState<PickedGuest | null>(null);
  const [isGuestPickerOpen, setIsGuestPickerOpen] = useState(false);
  const [guestName, setGuestName] = useState<string>("");
  const [guestPhone, setGuestPhone] = useState<string>("");
  const [guestEmail, setGuestEmail] = useState<string>("");
  const [guestIdNumber, setGuestIdNumber] = useState<string>("");

  const [checkIn, setCheckIn] = useState<string>("");
  const [checkOut, setCheckOut] = useState<string>("");
  const [adults, setAdults] = useState<number>(2);
  const [children, setChildren] = useState<number>(0);
  /** Plots of a unit type booked together (only when a unit type is picked). */
  const [plotCount, setPlotCount] = useState<number>(1);
  // Children are counted in the guests (capacity and per-guest extras use the total).
  const guestCount = (adults || 0) + (children || 0);
  const [source, setSource] = useState<BookingSource>("WHATSAPP");
  const [discount, setDiscount] = useState<number | string>(0);
  const [notes, setNotes] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const propId = initialPropertyId || (properties.length > 0 ? properties[0].id : "");
      setPropertyId(propId);
      if (initialUnitId) setUnitId(initialUnitId);

      const selectedProp = properties.find((p) => p.id === propId);
      const defaultCheckIn = getBusinessDate(selectedProp?.timezone);
      const checkInVal = initialCheckIn || defaultCheckIn;
      setCheckIn(checkInVal);

      const [y, m, d] = checkInVal.split("-").map(Number);
      const checkOutDate = new Date(Date.UTC(y, m - 1, d + 2));
      setCheckOut(checkOutDate.toISOString().split("T")[0]);
    }
  }, [isOpen, initialPropertyId, initialUnitId, initialCheckIn, properties]);

  /** "Register as new guest" from the picker: put what was typed into the field it most likely belongs to. */
  const startNewGuestFrom = (typed: string) => {
    setIsGuestPickerOpen(false);
    setGuestMode("new");
    setSelectedGuest(null);
    if (!typed) return;
    const compact = typed.replace(/[\s.\-]/g, "");
    if (/^\+?\d+$/.test(compact)) {
      // Indonesian phones start with 0, 62 or +; a 16-digit number that doesn't is a KTP number.
      if (/^(0|62|\+)/.test(compact)) setGuestPhone(typed);
      else setGuestIdNumber(typed);
    } else {
      setGuestName(typed);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchWithTenant(`/api/units?property_id=${propertyId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            // Only units that can take bookings: not in maintenance, inactive or archived.
            const bookable = (data.data as Unit[]).filter((u) => u.status === "ACTIVE");
            setUnits(bookable);
            if (!initialUnitId && bookable.length > 0) {
              setUnitId(bookable[0].id);
            }
          }
        })
        .catch(console.error);
    }
  }, [propertyId, initialUnitId, fetchWithTenant]);

  // Unit types (pools): booked as "any free plot", which the server picks.
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    fetchWithTenant(`/api/unit-groups?property_id=${propertyId}`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled && data.success) setGroups((data.data as UnitGroup[]).filter((g) => g.status === "ACTIVE" && (g.active_count ?? 0) > 0));
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, fetchWithTenant]);

  // Extras offered at checkout (firewood, breakfast...) and the ones picked, with their quantity (QUANTITY items only).
  const [addonItems, setAddonItems] = useState<CatalogItem[]>([]);
  const [addonQty, setAddonQty] = useState<Record<string, number>>({});
  useEffect(() => {
    setAddonQty({});
    if (!propertyId) return;
    let cancelled = false;
    fetchWithTenant(`/api/catalog?property_id=${propertyId}&status=ACTIVE`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled && data.success) setAddonItems((data.data as CatalogItem[]).filter((i) => i.checkout_addon === 1));
      })
      .catch(() => {
        if (!cancelled) setAddonItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, fetchWithTenant]);
  const addonsParam = Object.entries(addonQty).map(([id, qty]) => `${id}:${qty}`).join(",");

  // The property's active taxes and service charges, to show the same breakdown the server will bill.
  const [charges, setCharges] = useState<PropertyCharge[]>([]);
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    fetchWithTenant(`/api/properties/${propertyId}/charges`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled && data.success) setCharges((data.data as PropertyCharge[]).filter((c) => c.status === "ACTIVE"));
      })
      .catch(() => {
        if (!cancelled) setCharges([]);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, fetchWithTenant]);

  const selectedProp = properties.find((p) => p.id === propertyId);
  const currentCurrency = selectedProp?.currency || "IDR";

  const selectedGroup = unitId.startsWith(GROUP_PREFIX) ? groups.find((g) => `${GROUP_PREFIX}${g.id}` === unitId) ?? null : null;
  // A unit type is priced and sized like its plots (they're identical).
  const selectedUnit = useMemo((): Pick<Unit, "base_price" | "capacity" | "included_guests" | "extra_guest_price" | "child_price"> | null => {
    if (selectedGroup) return selectedGroup;
    return units.find((u) => u.id === unitId) || null;
  }, [units, unitId, selectedGroup]);
  const plots = selectedGroup ? Math.max(1, plotCount || 1) : 1;
  const unitParam = selectedGroup ? `unit_group_id=${encodeURIComponent(selectedGroup.id)}&units=${plots}` : `unit_id=${encodeURIComponent(unitId)}`;

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const s = new Date(checkIn);
    const e = new Date(checkOut);
    const diff = e.getTime() - s.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [checkIn, checkOut]);

  // The server prices the stay night by night (weekend/season rules); the flat figure only fills the gap while it loads.
  const [quote, setQuote] = useState<{ key: string; subtotal: number; addons: { catalog_item_id: string; units: number; amount: number }[]; addons_total: number } | null>(null);
  const quoteKey = `${unitId}|${checkIn}|${checkOut}|${guestCount}|${children}|${addonsParam}`;
  useEffect(() => {
    if (!isOpen || !unitId || nights <= 0 || !(adults >= 1)) return;
    let cancelled = false;
    const addonsQs = addonsParam ? `&addons=${encodeURIComponent(addonsParam)}` : "";
    fetchWithTenant(`/api/bookings/quote?${unitParam}&check_in=${checkIn}&check_out=${checkOut}&guests=${guestCount}&children=${children || 0}${addonsQs}`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled && data.success) setQuote({ key: quoteKey, subtotal: data.data.subtotal, addons: data.data.addons, addons_total: data.data.addons_total });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isOpen, unitId, unitParam, checkIn, checkOut, adults, guestCount, children, addonsParam, nights, quoteKey, fetchWithTenant]);

  const subtotal = useMemo(() => {
    if (!selectedUnit) return 0;
    if (quote && quote.key === quoteKey) return quote.subtotal;
    return nights * selectedUnit.base_price * plots;
  }, [selectedUnit, nights, plots, quote, quoteKey]);

  // Units of an extra for this stay (the server counts the same way; its quote replaces these once it arrives).
  const addonUnits = (basis: AddonBasis, qty: number) => {
    const guests = guestCount >= 1 ? guestCount : 1;
    return basis === "PER_GUEST" ? guests : basis === "PER_NIGHT" ? nights : basis === "PER_GUEST_NIGHT" ? guests * nights : qty;
  };
  const freshQuote = quote && quote.key === quoteKey ? quote : null;
  const addonsTotal = freshQuote
    ? freshQuote.addons_total
    : addonItems.filter((i) => addonQty[i.id] !== undefined).reduce((sum, i) => sum + i.price * addonUnits(i.addon_basis, addonQty[i.id]), 0);

  const discountMinor = useMemo(() => {
    try {
      return toMinorUnits(Number(discount) || 0, currentCurrency);
    } catch {
      return 0;
    }
  }, [discount, currentCurrency]);

  const breakdown = useMemo(
    () => computeCharges(Math.max(0, subtotal - discountMinor) + addonsTotal, charges.map((c) => ({ name: c.name, kind: c.kind, rate_bps: c.rate_bps, inclusive: c.inclusive }))),
    [subtotal, discountMinor, addonsTotal, charges]
  );
  const total = breakdown.total;

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const minorDiscount = toMinorUnits(Number(discount) || 0, currentCurrency);
      const payload: Record<string, unknown> = {
        property_id: propertyId,
        ...(selectedGroup ? { unit_group_id: selectedGroup.id, unit_count: plots } : { unit_id: unitId }),
        check_in: checkIn,
        check_out: checkOut,
        guest_count: guestCount,
        child_count: children || 0,
        source,
        discount: minorDiscount,
        notes: notes.trim() || undefined,
        addons: Object.entries(addonQty).map(([catalog_item_id, quantity]) => ({ catalog_item_id, quantity })),
      };

      if (guestMode === "existing") {
        if (!selectedGuest) {
          throw new Error(t("bookings.modal.errors.selectGuest"));
        }
        payload.guest_id = selectedGuest.id;
      } else {
        if (!guestName.trim()) {
          throw new Error(t("bookings.modal.errors.nameRequired"));
        }
        payload.guest_name = guestName.trim();
        payload.guest_phone = guestPhone.trim() || undefined;
        payload.guest_email = guestEmail.trim() || undefined;
        payload.guest_id_number = guestIdNumber.trim() || undefined;
      }

      const res = await fetchWithTenant("/api/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!json.success) {
        throw new Error(json.error || t("bookings.modal.errors.create"));
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("bookings.modal.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{t("bookings.modal.title")}</div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label={t("bookings.modal.close")}
            style={{ width: "24px", height: "24px", padding: 0 }}
          >
            <XIcon size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-banner">{error}</div>}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("bookings.modal.property")}</label>
                <select
                  className="form-control"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  required
                >
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t("bookings.modal.unit")}</label>
                <select
                  className="form-control"
                  value={unitId}
                  onChange={(e) => setUnitId(e.target.value)}
                  required
                >
                  {groups.length > 0 && (
                    <optgroup label={t("bookings.modal.unitGroups")}>
                      {groups.map((g) => (
                        <option key={g.id} value={`${GROUP_PREFIX}${g.id}`}>
                          {t("bookings.modal.groupOption", { name: g.name, count: g.active_count ?? 0, capacity: g.capacity })}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label={t("bookings.modal.singleUnits")}>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {t("bookings.modal.unitOption", { name: u.name, type: u.type, capacity: u.capacity })}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {selectedGroup && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                      <label className="form-label" style={{ margin: 0 }}>{t("bookings.modal.plotCount")}</label>
                      <input
                        type="number"
                        min={1}
                        max={selectedGroup.active_count ?? 1}
                        className="form-control"
                        style={{ width: 80 }}
                        value={plotCount}
                        onChange={(e) => setPlotCount(parseInt(e.target.value, 10))}
                      />
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{t("bookings.modal.groupHint")}</div>
                  </>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t("bookings.modal.dates")}</label>
              <DateRangeField
                start={checkIn}
                end={checkOut}
                onChange={(a, b) => { setCheckIn(a); setCheckOut(b); }}
                startLabel={t("bookings.modal.checkIn")}
                endLabel={t("bookings.modal.checkOut")}
                title={t("bookings.modal.dates")}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">{t("bookings.modal.adults")}</label>
                    <input type="number" min="1" className="form-control" value={adults} onChange={(e) => setAdults(parseInt(e.target.value, 10))} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">{t("bookings.modal.children")}</label>
                    <input type="number" min="0" className="form-control" value={children} onChange={(e) => setChildren(parseInt(e.target.value, 10))} />
                  </div>
                </div>
                {selectedUnit && selectedUnit.included_guests !== null && selectedUnit.included_guests !== undefined && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                    {t("units.perGuestSummary", { amount: formatMoney(selectedUnit.extra_guest_price ?? 0, currentCurrency), count: selectedUnit.included_guests })}
                    {selectedUnit.child_price !== null && selectedUnit.child_price !== undefined && ` · ${t("units.childPriceSummary", { amount: formatMoney(selectedUnit.child_price, currentCurrency) })}`}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">{t("bookings.modal.channel")}</label>
                <select
                  className="form-control"
                  value={source}
                  onChange={(e) => setSource(e.target.value as BookingSource)}
                  required
                >
                  {SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {tx(`bookings.source.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Guest: a new guest typed in here, or a registered guest found through the picker dialog */}
            <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>
              <div className="segmented" role="radiogroup" aria-label={t("bookings.modal.guestSection")}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={guestMode === "new"}
                  className={`segmented-option ${guestMode === "new" ? "active" : ""}`}
                  onClick={() => setGuestMode("new")}
                >
                  {t("bookings.modal.newGuest")}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={guestMode === "existing"}
                  className={`segmented-option ${guestMode === "existing" ? "active" : ""}`}
                  onClick={() => {
                    setGuestMode("existing");
                    if (!selectedGuest) setIsGuestPickerOpen(true);
                  }}
                >
                  {t("bookings.modal.existingGuest")}
                </button>
              </div>

              {guestMode === "existing" ? (
                selectedGuest ? (
                  <div className="picked-guest">
                    <span className="guest-picker-avatar">
                      <GuestIcon size={18} />
                    </span>
                    <div className="guest-picker-main">
                      <span className="guest-picker-name">{selectedGuest.name}</span>
                      <span className="guest-picker-meta">
                        {[
                          selectedGuest.phone,
                          selectedGuest.id_number_last4 ? t("bookings.guestPicker.idMasked", { last4: selectedGuest.id_number_last4 }) : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || t("bookings.guestPicker.noContact")}
                      </span>
                    </div>
                    <button type="button" className="btn btn-secondary" onClick={() => setIsGuestPickerOpen(true)}>
                      {t("bookings.modal.changeGuest")}
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-secondary picked-guest-empty" onClick={() => setIsGuestPickerOpen(true)}>
                    <SearchIcon size={16} />
                    <span>{t("bookings.modal.findGuest")}</span>
                  </button>
                )
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div className="form-group">
                    <label className="form-label">{t("bookings.modal.guestName")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("bookings.modal.guestNamePlaceholder")}
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      required={guestMode === "new"}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t("bookings.modal.phone")}</label>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder={t("bookings.modal.phonePlaceholder")}
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t("bookings.modal.email")}</label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder={t("bookings.modal.emailPlaceholder")}
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t("bookings.modal.idNumber")}</label>
                    <input
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      className="form-control"
                      placeholder={t("bookings.modal.idNumberPlaceholder")}
                      value={guestIdNumber}
                      onChange={(e) => setGuestIdNumber(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {isGuestPickerOpen && (
              <GuestPickerDialog
                onPick={(g) => {
                  setSelectedGuest(g);
                  setGuestMode("existing");
                  setIsGuestPickerOpen(false);
                }}
                onCreateNew={startNewGuestFrom}
                onClose={() => {
                  setIsGuestPickerOpen(false);
                  if (!selectedGuest) setGuestMode("new");
                }}
              />
            )}

            {/* Financial Summary */}
            <div className="form-row" style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "10px" }}>
              <div className="form-group">
                <label className="form-label">{t("bookings.modal.discount", { currency: currentCurrency })}</label>
                <input
                  type="number"
                  min="0"
                  step={getCurrencyDecimals(currentCurrency) === 0 ? "1" : "0.01"}
                  className="form-control"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t("bookings.modal.estimation")}</label>
                <div
                  style={{
                    background: "var(--bg-subtle)",
                    padding: "6px 10px",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-subtle)",
                    fontSize: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "34px",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>{t("bookings.modal.nights", { count: nights })}</span>
                  <span style={{ fontWeight: 600, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
                    {formatMoney(total, currentCurrency)}
                  </span>
                </div>
                {breakdown.lines.map((c) => (
                  <div key={c.name} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>
                    <span>{c.inclusive ? t("charges.included", { name: c.name, percent: c.rate_bps / 100 }) : t("charges.line", { name: c.name, percent: c.rate_bps / 100 })}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{c.inclusive ? "" : "+"}{formatMoney(c.amount, currentCurrency)}</span>
                  </div>
                ))}
              </div>
            </div>

            {addonItems.length > 0 && (
              <div className="form-group">
                <label className="form-label">{t("bookings.modal.addons")}</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {addonItems.map((item) => {
                    const picked = addonQty[item.id] !== undefined;
                    const units = addonUnits(item.addon_basis, addonQty[item.id] ?? 1);
                    const amount = freshQuote?.addons.find((a) => a.catalog_item_id === item.id)?.amount ?? item.price * units;
                    return (
                      <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={picked}
                            onChange={(e) => {
                              const next = { ...addonQty };
                              if (e.target.checked) next[item.id] = 1;
                              else delete next[item.id];
                              setAddonQty(next);
                            }}
                          />
                          <span>
                            {item.name} <span style={{ color: "var(--text-muted)" }}>· {formatMoney(item.price, currentCurrency)} · {tx(`catalog.addonBasis.${item.addon_basis}`)}</span>
                          </span>
                        </label>
                        {picked && item.addon_basis === "QUANTITY" && (
                          <input
                            type="number"
                            min="1"
                            max="99"
                            className="form-control"
                            style={{ width: 64, height: 28 }}
                            aria-label={item.name}
                            value={addonQty[item.id]}
                            onChange={(e) => setAddonQty({ ...addonQty, [item.id]: Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)) })}
                          />
                        )}
                        {picked && (
                          <span style={{ fontVariantNumeric: "tabular-nums", minWidth: 90, textAlign: "right" }}>
                            {t("bookings.modal.addonUnits", { count: units })} · {formatMoney(amount, currentCurrency)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{t("bookings.modal.addonsHint")}</div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t("bookings.modal.notes")}</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder={t("bookings.modal.notesPlaceholder")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClose}
              disabled={loading}
            >
              {t("bookings.modal.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={loading || nights <= 0}
            >
              {loading ? t("bookings.modal.submitting") : t("bookings.modal.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
